import { inject, injectable } from '@wix/typesafe-inversify';
import type { EditorSDK, IGetDataByAppDef } from '@wix/platform-editor-sdk';
import type { IAppDescriptorApi } from '@wix/blocks-widget-services/editor';
import { getAppDescriptorByAppData } from '@wix/blocks-widget-services/editor';
import type { BlocksAppDescriptor } from '@wix/document-services-types';
import type { IAppDescriptorService } from './IAppDescriptorService';
import assert from 'assert';
import { diff } from 'json-diff';
import { ExperimentNames } from '../Experiments/ExperimentNames';
import type { IExperiments } from '../Experiments/IExperiments';
import type { IReadyAppData } from '../ReadyAppData/IReadyAppData';
import { Services } from '../../ioc/services';
import type {
  IPlatformHttpClient,
  IPlatformPanorama,
} from '../../types/PlatformTypes';
import { monitored } from '../Monitoring/monitored';
import type { IMonitoring } from '../Monitoring/IMonitoring';

const token = 'token';

@injectable()
export class AppDescriptorService implements IAppDescriptorService {
  private siteHeaderUrl?: string;
  private cachedAppDescriptor?: BlocksAppDescriptor;

  constructor(
    @inject(Services.EditorSDK)
    private editorSDK: EditorSDK,
    @inject(Services.Experiments)
    private experiments: IExperiments,
    @inject(Services.ReadyAppData)
    private readyAppDataService: IReadyAppData,
    @inject(Services.Monitoring)
    private readonly monitoring: IMonitoring,
    @inject(Services.PlatformHttpClient)
    private readonly platformHttpClient: IPlatformHttpClient,
    @inject(Services.PlatformPanorama)
    private readonly platformPanorama: IPlatformPanorama,
    @inject(Services.AppDescriptorApi)
    private readonly appDescriptorApi: IAppDescriptorApi,
  ) {}

  async get() {
    const appData = await this.editorSDK.tpa.app.getDataByAppDefId(
      token,
      this.readyAppDataService.appDefinitionId,
    );
    assert.strictEqual(
      this.readyAppDataService.appDefinitionId,
      appData.appDefinitionId,
      'App defs ids are different',
    );
    const appSiteHeaderUrl = appData.appFields.platform?.studio?.siteHeaderUrl;
    if (appSiteHeaderUrl !== this.siteHeaderUrl) {
      this.cachedAppDescriptor = await this.getAppDescriptorImpl(
        appSiteHeaderUrl,
        appData,
      );
    }
    return this.cachedAppDescriptor!;
  }

  @monitored('get-app-descriptor-platformized')
  private async getAppDescriptorImplNew(appData: IGetDataByAppDef) {
    return this.appDescriptorApi.getAppDescriptor({ appData });
  }

  @monitored('get-app-descriptor-legacy')
  private async getAppDescriptorImplOld(
    appSiteHeaderUrl: string | undefined,
    appData: IGetDataByAppDef,
  ) {
    const isGetSiteAssetVersionDynamically = this.experiments?.enabled(
      ExperimentNames.GetSiteAssetVersionDynamically,
    );
    this.siteHeaderUrl = appSiteHeaderUrl;
    const transaction = this.monitoring.transaction(
      'get-app-descriptor-from-site-assets',
    );
    transaction.start();
    let failedFetchingFromSiteAsserts = false;
    const descriptor = await getAppDescriptorByAppData(appData, {
      httpClient: this.platformHttpClient,
      editorSDK: isGetSiteAssetVersionDynamically ? this.editorSDK : undefined,
      onSiteAssetsFetchError: (e) => {
        failedFetchingFromSiteAsserts = true;
        transaction.reportError(e);
      },
    });

    if (!failedFetchingFromSiteAsserts) {
      transaction.stop();
    }
    return descriptor;
  }

  @monitored('get-app-descriptor')
  private async getAppDescriptorImpl(
    appSiteHeaderUrl: string | undefined,
    appData: IGetDataByAppDef,
  ) {
    const value =
      this.experiments.value(ExperimentNames.UsePlatformizedAppDescriptor) ??
      'false';

    if (value.includes('shadow')) {
      const preferNew = value === 'true-shadow';
      const [
        { result: newResult, elapsed: elapsedTrue },
        { result: oldResult, elapsed: elapsedFalse },
      ] = await Promise.all([
        this.timed(() => this.getAppDescriptorImplNew(appData)),
        this.timed(() =>
          this.getAppDescriptorImplOld(appSiteHeaderUrl, appData),
        ),
      ]);
      void this.compareDescriptorResults(newResult, oldResult, {
        preferNew,
        elapsedTrue,
        elapsedFalse,
      });
      return this.pickSettledDescriptorResult(newResult, oldResult, preferNew);
    }

    if (value === 'true') {
      return this.getAppDescriptorImplNew(appData);
    }

    return this.getAppDescriptorImplOld(appSiteHeaderUrl, appData);
  }

  private pickSettledDescriptorResult(
    newResult: PromiseSettledResult<BlocksAppDescriptor>,
    oldResult: PromiseSettledResult<BlocksAppDescriptor>,
    preferNew: boolean,
  ): BlocksAppDescriptor {
    if (preferNew) {
      if (newResult.status === 'fulfilled') {
        return newResult.value;
      }
      if (oldResult.status === 'fulfilled') {
        return oldResult.value;
      }
      throw newResult.reason;
    }
    if (oldResult.status === 'fulfilled') {
      return oldResult.value;
    }
    throw oldResult.reason;
  }

  private async compareDescriptorResults(
    newResult: PromiseSettledResult<BlocksAppDescriptor>,
    oldResult: PromiseSettledResult<BlocksAppDescriptor>,
    ctx: { preferNew: boolean; elapsedTrue: number; elapsedFalse: number },
  ) {
    const logger = this.platformPanorama.logger();
    const userInfo = await this.editorSDK.info
      .getUserInfo(token)
      .catch(() => null);

    const isEqual =
      newResult.status === 'fulfilled' &&
      oldResult.status === 'fulfilled' &&
      this.sortedStringify(this.normalizeForComparison(newResult.value)) ===
        this.sortedStringify(this.normalizeForComparison(oldResult.value));

    logger.info('shadow: app-descriptor', {
      isShadow: true,
      isEnabled: ctx.preferNew,
      isEqual,
      statusTrue: newResult.status,
      statusFalse: oldResult.status,
      elapsedTrue: ctx.elapsedTrue,
      elapsedFalse: ctx.elapsedFalse,
      ...(!isEqual &&
        newResult.status === 'fulfilled' &&
        oldResult.status === 'fulfilled' && {
          valueDiff: diff(
            this.normalizeForComparison(newResult.value),
            this.normalizeForComparison(oldResult.value),
          ),
        }),
      ...(newResult.status === 'rejected' && {
        errorTrue: String(newResult.reason),
      }),
      ...(oldResult.status === 'rejected' && {
        errorFalse: String(oldResult.reason),
      }),
      isWixStaff: userInfo?.email?.endsWith('@wix.com') ?? false,
    });
  }

  private async timed<T>(
    fn: () => Promise<T>,
  ): Promise<{ result: PromiseSettledResult<T>; elapsed: number }> {
    const start = Date.now();
    try {
      const value = await fn();
      return {
        result: { status: 'fulfilled', value },
        elapsed: Date.now() - start,
      };
    } catch (reason) {
      return {
        result: { status: 'rejected', reason },
        elapsed: Date.now() - start,
      };
    }
  }

  /**
   * Normalize a descriptor for shadow comparison:
   * - strips `languages` (legacy fetch omits it)
   * - rounds preset defaultSize dimensions to avoid float-precision false mismatches
   */
  private normalizeForComparison(
    descriptor: BlocksAppDescriptor,
  ): Record<string, unknown> {
    const { languages: _languages, ...rest } =
      descriptor as BlocksAppDescriptor & {
        languages?: unknown;
        widgets?: Record<string, any>;
      };

    if (rest.widgets && typeof rest.widgets === 'object') {
      rest.widgets = Object.fromEntries(
        Object.entries(rest.widgets as Record<string, any>).map(
          ([id, widget]) => [
            id,
            {
              ...widget,
              presets: Array.isArray(widget.presets)
                ? widget.presets.map((preset: any) =>
                    preset?.defaultSize
                      ? {
                          ...preset,
                          defaultSize: {
                            ...preset.defaultSize,
                            ...(preset.defaultSize.height !== undefined && {
                              height: Math.round(preset.defaultSize.height),
                            }),
                            ...(preset.defaultSize.width !== undefined && {
                              width: Math.round(preset.defaultSize.width),
                            }),
                          },
                        }
                      : preset,
                  )
                : widget.presets,
            },
          ],
        ),
      );
    }

    return rest as Record<string, unknown>;
  }

  private sortedStringify(value: unknown): string {
    return JSON.stringify(value, (_, v) =>
      v && typeof v === 'object' && !Array.isArray(v)
        ? Object.fromEntries(
            Object.entries(v).sort(([a], [b]) => a.localeCompare(b)),
          )
        : v,
    );
  }
}
